import { compareSmetaGroupSort } from "./smetaNewTableRows.js";

/**
 * Подпись узла «блок» в дереве сметы по ЖК.
 * — block_name, entrance: из API (после обогащения из project_entrance_tab);
 * — project_name: имя цифрового проекта (project_tab.project_name).
 */
/** Подпись блока/подъезда для фильтра (project_entrance_tab). */
export function formatResidentSmetaBlockEntranceLabel(item) {
  const bn = item?.block_name;
  const ent = item?.entrance;
  if (bn != null && bn !== "" && ent != null && ent !== "") {
    return `Блок ${bn} (Подъезд ${ent})`;
  }
  if (bn != null && bn !== "") {
    return `Блок ${bn}`;
  }
  if (ent != null && ent !== "") {
    return `Подъезд ${ent}`;
  }
  return "—";
}

export function formatResidentSmetaBlockTitle(row, projectId) {
  const label = formatResidentSmetaBlockEntranceLabel(row);
  if (label !== "—") return label;
  return row?.project_name || String(projectId || "—");
}

export function formatResidentSmetaBlockProjectIdLine(row, projectId) {
  const pid = row?.project_id ?? projectId;
  if (pid == null || pid === "") return null;
  return `ID проекта: ${pid}`;
}

/** Одна строка (для поиска/совместимости); в UI предпочтительны title + projectIdLine. */
export function formatResidentSmetaBlockLabel(row, projectId) {
  const title = formatResidentSmetaBlockTitle(row, projectId);
  const subline = formatResidentSmetaBlockProjectIdLine(row, projectId);
  if (subline && title !== "—") return `${title}\n${subline}`;
  if (subline) return subline;
  return title;
}

function roundCost(n) {
  return Math.round(n * 100) / 100;
}

function emptyCostBucket() {
  return { wsum: 0, msum: 0, ttl: 0, ttl_nds: 0 };
}

function addRowCostToBucket(bucket, row) {
  if (row.wsum_no_nds != null) bucket.wsum += Number(row.wsum_no_nds) || 0;
  if (row.msum_no_nds != null) bucket.msum += Number(row.msum_no_nds) || 0;
  if (row.ttl_no_nds != null) bucket.ttl += Number(row.ttl_no_nds) || 0;
  if (row.ttl != null) bucket.ttl_nds += Number(row.ttl) || 0;
}

function applyCostsToNode(node, bucket) {
  node.wsum_cost = roundCost(bucket.wsum);
  node.msum_cost = roundCost(bucket.msum);
  node.total_cost = roundCost(bucket.ttl);
  node.total_cost_with_nds = roundCost(bucket.ttl_nds);
}

function getBlockKey(row) {
  if (row.project_entrance_id != null && row.project_entrance_id !== "") {
    return `pe:${row.project_entrance_id}`;
  }
  return `legacy:${row.project_id}__${row.entrance ?? ""}`;
}

function getTypeBlockKey(tname, blockKey) {
  return `${tname}|${blockKey}`;
}

function getFloorScopeKey(typeBlockKey, levelNum) {
  return `${typeBlockKey}|${String(levelNum ?? "")}`;
}

function getGroupScopeKey(floorScopeKey, groupId) {
  return `${floorScopeKey}|${String(groupId ?? "nogroup")}`;
}

/**
 * Один проход по tableRows: суммы по уровням дерева, работы и строки материалов.
 */
function buildResidentSmetaTableIndex(tableRows) {
  const root = emptyCostBucket();
  const byType = new Map();
  const byTypeBlock = new Map();
  const byFloor = new Map();
  const byGroup = new Map();
  const workCostByGroupKey = new Map();
  const materialSumByGroupKey = new Map();
  const materialRowsByGroupKey = new Map();
  const worksByGroupScope = new Map();

  if (!tableRows?.length) {
    return {
      root,
      byType,
      byTypeBlock,
      byFloor,
      byGroup,
      workCostByGroupKey,
      materialSumByGroupKey,
      materialRowsByGroupKey,
      worksByGroupScope,
    };
  }

  const ensureBucket = (map, key) => {
    let bucket = map.get(key);
    if (!bucket) {
      bucket = emptyCostBucket();
      map.set(key, bucket);
    }
    return bucket;
  };

  for (const row of tableRows) {
    if (row.isGroupHeader) continue;

    const tname = row.project_type_name || "—";
    const blockKey = getBlockKey(row);
    const typeBlockKey = getTypeBlockKey(tname, blockKey);
    const floorScopeKey = getFloorScopeKey(typeBlockKey, row.level_num);
    const groupScopeKey = getGroupScopeKey(
      floorScopeKey,
      row.work_set_check_group_id
    );

    addRowCostToBucket(root, row);
    addRowCostToBucket(ensureBucket(byType, tname), row);
    addRowCostToBucket(ensureBucket(byTypeBlock, typeBlockKey), row);
    addRowCostToBucket(ensureBucket(byFloor, floorScopeKey), row);
    addRowCostToBucket(ensureBucket(byGroup, groupScopeKey), row);

    if (!row.groupKey) continue;

    addRowCostToBucket(ensureBucket(workCostByGroupKey, row.groupKey), row);

    const materialSum =
      Number(row.msum_line_no_nds ?? row.msum_no_nds) || 0;
    materialSumByGroupKey.set(
      row.groupKey,
      (materialSumByGroupKey.get(row.groupKey) || 0) + materialSum
    );

    let materialRows = materialRowsByGroupKey.get(row.groupKey);
    if (!materialRows) {
      materialRows = [];
      materialRowsByGroupKey.set(row.groupKey, materialRows);
    }
    materialRows.push(row);

    let worksMap = worksByGroupScope.get(groupScopeKey);
    if (!worksMap) {
      worksMap = new Map();
      worksByGroupScope.set(groupScopeKey, worksMap);
    }
    if (!worksMap.has(row.groupKey)) {
      worksMap.set(row.groupKey, {
        groupKey: row.groupKey,
        work_set_id: row.work_set_id,
        label: row.work_set_name || row.wname || "Работа",
        wunt: row.wunt || row.work_set_unit_name || "",
        wqty: row.wqty,
        wprc_no_nds: row.wprc_no_nds,
      });
    }
  }

  for (const [key, sum] of materialSumByGroupKey) {
    materialSumByGroupKey.set(key, roundCost(sum));
  }

  return {
    root,
    byType,
    byTypeBlock,
    byFloor,
    byGroup,
    workCostByGroupKey,
    materialSumByGroupKey,
    materialRowsByGroupKey,
    worksByGroupScope,
  };
}

/** Один проход по rawRows: иерархия тип → блок → этаж → группа. */
function buildResidentSmetaRawIndex(rawRows) {
  const types = new Map();

  for (const row of rawRows) {
    const tname = row.project_type_name || "—";
    let typeEntry = types.get(tname);
    if (!typeEntry) {
      typeEntry = { blocks: new Map() };
      types.set(tname, typeEntry);
    }

    const blockKey = getBlockKey(row);
    let blockEntry = typeEntry.blocks.get(blockKey);
    if (!blockEntry) {
      blockEntry = { sample: row, levels: new Map() };
      typeEntry.blocks.set(blockKey, blockEntry);
    }

    const levelKey = String(row.level_num ?? "");
    let levelEntry = blockEntry.levels.get(levelKey);
    if (!levelEntry) {
      levelEntry = { sample: row, groups: new Map() };
      blockEntry.levels.set(levelKey, levelEntry);
    }

    const gid = String(row.work_set_check_group_id ?? "nogroup");
    if (!levelEntry.groups.has(gid)) {
      levelEntry.groups.set(gid, row);
    }
  }

  return { types };
}

function buildBlockNode(tname, blockKey, sample) {
  if (blockKey.startsWith("legacy:")) {
    const rest = blockKey.slice("legacy:".length);
    const sep = rest.indexOf("__");
    const project_id = Number(rest.slice(0, sep));
    const entrance = rest.slice(sep + 2);
    const label = formatResidentSmetaBlockTitle(sample, project_id);
    const projectIdLine = formatResidentSmetaBlockProjectIdLine(
      sample,
      project_id
    );
    return {
      id: `b:${blockKey}`,
      node_type: "block",
      label,
      projectIdLine,
      expanded: false,
      blockKey,
      project_id: sample?.project_id ?? project_id,
      project_entrance_id: sample?.project_entrance_id ?? null,
      entrance: sample?.entrance ?? entrance,
      children: [],
    };
  }

  const project_entrance_id = Number(blockKey.slice(3));
  const project_id = sample?.project_id;
  const label = formatResidentSmetaBlockTitle(sample, project_id);
  const projectIdLine = formatResidentSmetaBlockProjectIdLine(
    sample,
    project_id
  );
  return {
    id: `b:${blockKey}`,
    node_type: "block",
    label,
    projectIdLine,
    expanded: false,
    blockKey,
    project_id,
    project_entrance_id,
    entrance: sample?.entrance,
    children: [],
  };
}

function buildWorkNodesUnderGroup(groupNode, tableIndex, groupScopeKey) {
  const worksMap = tableIndex.worksByGroupScope.get(groupScopeKey);
  if (!worksMap?.size) return [];

  const workNodes = [];
  const works = [...worksMap.values()].sort((a, b) =>
    String(a.label).localeCompare(String(b.label), "ru")
  );

  for (const w of works) {
    const workBucket = tableIndex.workCostByGroupKey.get(w.groupKey);
    const materialRows =
      tableIndex.materialRowsByGroupKey.get(w.groupKey) || [];

    const materialsNode = {
      id: `mat:${groupNode.id}:${w.groupKey}`,
      node_type: "materials",
      label: "Материалы",
      expanded: false,
      groupKey: w.groupKey,
      work_set_id: w.work_set_id,
      project_id: groupNode.project_id,
      project_entrance_id: groupNode.project_entrance_id,
      entrance: groupNode.entrance,
      level_num: groupNode.level_num,
      work_set_check_group_id: groupNode.work_set_check_group_id,
      materialRows,
      children: [],
    };

    const materialSum = tableIndex.materialSumByGroupKey.get(w.groupKey);
    if (materialSum != null) {
      materialsNode.msum_cost = materialSum;
    }

    const workNode = {
      id: `wrk:${groupNode.id}:${w.groupKey}`,
      node_type: "work",
      label: w.label,
      wunt: w.wunt,
      wqty: w.wqty,
      wprc_no_nds: w.wprc_no_nds,
      expanded: false,
      groupKey: w.groupKey,
      work_set_id: w.work_set_id,
      project_id: groupNode.project_id,
      project_entrance_id: groupNode.project_entrance_id,
      entrance: groupNode.entrance,
      level_num: groupNode.level_num,
      work_set_check_group_id: groupNode.work_set_check_group_id,
      children: [materialsNode],
    };

    if (workBucket) {
      applyCostsToNode(workNode, workBucket);
    }

    workNodes.push(workNode);
  }

  return workNodes;
}

/**
 * Сумма по полю работы (значение только на первой строке работы в buildSmetaNewTableRows).
 * @param {Array<object>} tableRows
 * @param {(r: object) => boolean} predicate
 * @param {'wsum_no_nds'|'msum_no_nds'|'ttl_no_nds'} field
 */
export function sumResidentSmetaField(tableRows, predicate, field) {
  if (!tableRows?.length) return 0;
  let s = 0;
  for (const r of tableRows) {
    if (r.isGroupHeader) continue;
    if (r[field] == null) continue;
    if (!predicate(r)) continue;
    s += Number(r[field]) || 0;
  }
  return roundCost(s);
}

/** @deprecated используйте sumResidentSmetaField(..., 'ttl_no_nds') */
export function sumResidentSmetaWorkTotals(tableRows, predicate) {
  return sumResidentSmetaField(tableRows, predicate, "ttl_no_nds");
}

/**
 * Дерево: ЖК → тип проекта → блок → этаж → группа работ → работа (строка) → материалы (подпапка).
 * Узел «блок» группируется по project_entrance_id (подъезд в рамках проекта).
 * @param {Array<object>} rawRows — строки API resident_smeta_new_report
 * @param {string} residentLabel
 * @param {Array<object>} [tableRows] — строки buildSmetaNewTableRows (для total_cost по ttl_no_nds)
 */
export function buildResidentSmetaTree(rawRows, residentLabel, tableRows) {
  if (!rawRows?.length) return [];

  const tableIndex = buildResidentSmetaTableIndex(tableRows || []);
  const rawIndex = buildResidentSmetaRawIndex(rawRows);
  const hasTable = !!tableRows?.length;

  const rootLabel =
    residentLabel || rawRows[0]?.resident_name || "Жилой комплекс";

  const root = {
    id: "jk",
    node_type: "resident",
    label: rootLabel,
    expanded: true,
    children: [],
  };

  if (hasTable) {
    applyCostsToNode(root, tableIndex.root);
  }

  const typeNames = [...rawIndex.types.keys()].sort((a, b) =>
    a.localeCompare(b, "ru")
  );

  for (const tname of typeNames) {
    const typeData = rawIndex.types.get(tname);
    const typeSample = [...typeData.blocks.values()][0]?.sample;
    const typeNode = {
      id: `t:${tname}`,
      node_type: "project_type",
      label: tname,
      project_type_id: typeSample?.project_type_id ?? null,
      expanded: false,
      children: [],
    };

    if (hasTable) {
      const typeBucket = tableIndex.byType.get(tname);
      if (typeBucket) applyCostsToNode(typeNode, typeBucket);
    }

    const blockList = [...typeData.blocks.entries()]
      .map(([blockKey, blockData]) =>
        buildBlockNode(tname, blockKey, blockData.sample)
      )
      .sort((a, b) => a.label.localeCompare(b.label, "ru"));

    for (const blockNode of blockList) {
      const typeBlockKey = getTypeBlockKey(tname, blockNode.blockKey);
      const blockData = typeData.blocks.get(blockNode.blockKey);

      if (hasTable) {
        const blockBucket = tableIndex.byTypeBlock.get(typeBlockKey);
        if (blockBucket) applyCostsToNode(blockNode, blockBucket);
      }

      const floors = [...blockData.levels.entries()]
        .map(([levelKey, levelData]) => {
          const level_num = levelData.sample?.level_num;
          const floorSample = levelData.sample;
          const floorNode = {
            id: `f:${blockNode.id}:${levelKey}`,
            node_type: "floor",
            label: `Этаж ${level_num ?? "—"}`,
            expanded: false,
            project_id: blockNode.project_id,
            project_entrance_id: blockNode.project_entrance_id,
            entrance: blockNode.entrance,
            level_num,
            floor_map_id: floorSample?.floor_map_id ?? null,
            children: [],
          };

          const floorScopeKey = getFloorScopeKey(typeBlockKey, level_num);
          if (hasTable) {
            const floorBucket = tableIndex.byFloor.get(floorScopeKey);
            if (floorBucket) applyCostsToNode(floorNode, floorBucket);
          }

          const groups = [...levelData.groups.entries()]
            .map(([gid, sample]) => {
              const groupScopeKey = getGroupScopeKey(floorScopeKey, gid);
              const groupNode = {
                id: `g:${blockNode.id}:${floorNode.level_num}:${gid}`,
                node_type: "group",
                label: sample?.work_set_check_group_name || "Группа работ",
                expanded: false,
                project_id: blockNode.project_id,
                project_entrance_id: blockNode.project_entrance_id,
                entrance: blockNode.entrance,
                level_num: floorNode.level_num,
                work_set_check_group_id: sample?.work_set_check_group_id,
                group_order_num: sample?.group_order_num,
                source_work_set_check_group_id:
                  sample?.source_work_set_check_group_id,
                children: [],
              };

              if (hasTable) {
                const groupBucket = tableIndex.byGroup.get(groupScopeKey);
                if (groupBucket) applyCostsToNode(groupNode, groupBucket);
                groupNode.children = buildWorkNodesUnderGroup(
                  groupNode,
                  tableIndex,
                  groupScopeKey
                );
              }

              return groupNode;
            })
            .sort(compareSmetaGroupSort);

          floorNode.children = groups;
          return floorNode;
        })
        .sort((a, b) => Number(a.level_num ?? 0) - Number(b.level_num ?? 0));

      blockNode.children = floors;
    }

    typeNode.children = blockList;
    root.children.push(typeNode);
  }

  return [root];
}

/**
 * Этажи для фильтра из строк сметы (если floor_map/read пустой).
 * @param {Array<object>} rawRows
 * @param {{ project_entrance_id?: number|string|null }} scope
 */
export function buildResidentSmetaFloorOptionsFromRaw(rawRows, scope = {}) {
  const entranceId = scope.project_entrance_id;
  const floorsMap = new Map();

  for (const row of rawRows || []) {
    if (
      entranceId != null &&
      entranceId !== "" &&
      String(row.project_entrance_id) !== String(entranceId)
    ) {
      continue;
    }

    const floor_map_id = row.floor_map_id;
    if (floor_map_id == null || floor_map_id === "") continue;

    const key = String(floor_map_id);
    if (floorsMap.has(key)) continue;

    const levelNum = row.level_num ?? row.floor;
    floorsMap.set(key, {
      value: Number(floor_map_id),
      level_num: levelNum,
      label: `Этаж ${levelNum ?? "—"}`,
      project_entrance_id: row.project_entrance_id,
    });
  }

  return [...floorsMap.values()].sort(
    (a, b) => Number(a.level_num ?? 0) - Number(b.level_num ?? 0)
  );
}

/**
 * Справочники для фильтров из строк resident_agreement_smeta_report (без отдельных read-функций).
 */
export function buildResidentSmetaFilterOptionsFromRaw(rawRows) {
  const groupsMap = new Map();
  const worksMap = new Map();
  const materialsMap = new Map();

  for (const row of rawRows || []) {
    const gid = row.work_set_check_group_id;
    if (gid != null && gid !== "") {
      const key = String(gid);
      if (!groupsMap.has(key)) {
        groupsMap.set(key, {
          value: Number(gid),
          label: row.work_set_check_group_name || String(gid),
        });
      }
    }

    const wid = row.work_set_id;
    if (wid != null && wid !== "") {
      const key = String(wid);
      if (!worksMap.has(key)) {
        worksMap.set(key, {
          value: Number(wid),
          label: row.work_set_name || String(wid),
        });
      }
    }

    const mid = row.material_id;
    if (mid != null && mid !== "" && Number(mid) !== 0) {
      const key = String(mid);
      if (!materialsMap.has(key)) {
        const name = row.material_name || "";
        materialsMap.set(key, {
          value: Number(mid),
          label: name ? `${mid} / ${name}` : String(mid),
        });
      }
    }
  }

  const sortByLabel = (a, b) =>
    String(a.label).localeCompare(String(b.label), "ru");

  return {
    workGroups: [...groupsMap.values()].sort(sortByLabel),
    workSets: [...worksMap.values()].sort(sortByLabel),
    materials: [...materialsMap.values()].sort(sortByLabel),
  };
}

export function findResidentSmetaNodeById(nodes, id) {
  if (!nodes?.length || id == null) return null;
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findResidentSmetaNodeById(node.children, id);
    if (found) return found;
  }
  return null;
}

/** Путь id от корня до первого узла, удовлетворяющего predicate (включая сам узел). */
export function collectResidentSmetaExpandPath(nodes, predicate, path = []) {
  if (!nodes?.length) return null;
  for (const node of nodes) {
    const nextPath = [...path, node.id];
    if (predicate(node)) return nextPath;
    const found = collectResidentSmetaExpandPath(node.children, predicate, nextPath);
    if (found) return found;
  }
  return null;
}

function matchFloorNode(n, floor_map_id, level_num) {
  if (n.node_type !== "floor") return false;
  if (floor_map_id != null && floor_map_id !== "") {
    if (n.floor_map_id != null && String(n.floor_map_id) === String(floor_map_id)) {
      return true;
    }
  }
  if (level_num != null && level_num !== "") {
    return String(n.level_num) === String(level_num);
  }
  return false;
}

/** Значение фильтра считается активным (не «Все» / пусто / 0). */
export function isResidentSmetaFilterValueActive(value) {
  return value != null && value !== "" && Number(value) !== 0;
}

/**
 * Id узлов для раскрытия дерева по активным фильтрам (без раскрытия дочерних уровней ниже цели).
 */
export function getResidentSmetaExpandedIdsForFilters(treeRoots, filters) {
  const ids = new Set();
  if (!treeRoots?.length) return ids;

  const project_type_id = filters?.project_type_id;
  const project_entrance_id = filters?.project_entrance_id;
  const floor_map_id = filters?.floor_map_id;
  const level_num = filters?.level_num;
  const work_set_check_group_id = filters?.work_set_check_group_id;
  const work_set_id = filters?.work_set_id;
  const material_id = filters?.material_id;

  function addPath(path, { expandMaterialsForWork = false } = {}) {
    if (!path?.length) return;
    for (const id of path) ids.add(id);
    if (!expandMaterialsForWork) return;
    const lastId = path[path.length - 1];
    const node = findResidentSmetaNodeById(treeRoots, lastId);
    if (node?.node_type === "work") {
      const matChild = node.children?.find((c) => c.node_type === "materials");
      if (matChild) ids.add(matChild.id);
    }
  }

  if (isResidentSmetaFilterValueActive(material_id)) {
    const path = collectResidentSmetaExpandPath(treeRoots, (n) =>
      n.node_type === "materials" &&
      (n.materialRows || []).some(
        (r) => Number(r.material_id) === Number(material_id)
      )
    );
    addPath(path, { expandMaterialsForWork: true });
    if (path) return ids;
  }

  if (isResidentSmetaFilterValueActive(work_set_id)) {
    const path = collectResidentSmetaExpandPath(treeRoots, (n) =>
      n.node_type === "work" && String(n.work_set_id) === String(work_set_id)
    );
    addPath(path, { expandMaterialsForWork: true });
    if (path) return ids;
  }

  if (isResidentSmetaFilterValueActive(work_set_check_group_id)) {
    const path = collectResidentSmetaExpandPath(treeRoots, (n) =>
      n.node_type === "group" &&
      String(n.work_set_check_group_id) === String(work_set_check_group_id)
    );
    addPath(path);
    return ids;
  }

  if (
    isResidentSmetaFilterValueActive(floor_map_id) ||
    isResidentSmetaFilterValueActive(level_num)
  ) {
    const path = collectResidentSmetaExpandPath(treeRoots, (n) =>
      matchFloorNode(n, floor_map_id, level_num)
    );
    addPath(path);
    if (path) return ids;
  }

  if (isResidentSmetaFilterValueActive(project_entrance_id)) {
    const path = collectResidentSmetaExpandPath(treeRoots, (n) =>
      n.node_type === "block" &&
      String(n.project_entrance_id) === String(project_entrance_id)
    );
    addPath(path);
    if (path) return ids;
  }

  if (isResidentSmetaFilterValueActive(project_type_id)) {
    const path = collectResidentSmetaExpandPath(treeRoots, (n) =>
      n.node_type === "project_type" &&
      String(n.project_type_id) === String(project_type_id)
    );
    addPath(path);
    if (path) return ids;
  }

  return ids;
}

/** Все id узлов дерева, которые можно раскрыть (включая папки «Материалы»). */
export function collectResidentSmetaExpandableIds(nodes, out = []) {
  if (!nodes?.length) return out;
  for (const n of nodes) {
    const hasChildren = Array.isArray(n.children) && n.children.length > 0;
    if (hasChildren || n.node_type === "materials") {
      out.push(n.id);
    }
    if (hasChildren) {
      collectResidentSmetaExpandableIds(n.children, out);
    }
  }
  return out;
}
